import { PageHeader, LoadingState, DataTable, FilterBar, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, TextField, MenuItem, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, useMediaQuery, useTheme, alpha, Stack, Divider, Tabs, Tab } from '@mui/material';
import { keyframes } from '@mui/system';
import {
    DirectionsBus as BusIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Payments as PaymentsIcon,
    PersonAdd as PersonAddIcon,
    Search as SearchIcon,
    School as SchoolIcon,
    Today as TodayIcon,
    RemoveCircle as RemoveCircleIcon,
    MoneyOff as MoneyOffIcon,
    Summarize as SummarizeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { startOfWeek, addDays, format } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const BOARDING_POLL_INTERVAL_MS = 30000;

const pulseGreen = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(73, 134, 122, 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(73, 134, 122, 0); }
  100% { box-shadow: 0 0 0 0 rgba(73, 134, 122, 0); }
`;

export default function TransportClassBoardingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);

  // Selection State based on School Standard
  const [academicYear, setAcademicYear] = useState('');
  const userSelectedAcademicYearRef = useRef(false);
  const [term, setTerm] = useState('');
  const [week, setWeek] = useState('');
  const [selectedTermId, setSelectedTermId] = useState('');

  const [allTerms, setAllTerms] = useState([]);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);

  const [selectedDate, setSelectedDate] = useState(new Date());

  // Data State
  const [allStudents, setAllStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const abortControllerRef = React.useRef(null);
  const studentsRef = React.useRef([]);
  const backgroundFetchInFlightRef = React.useRef(false);
  const lastNetworkToastAtRef = React.useRef(0);

  // Weekly Grid States
  const [markingStatus, setMarkingStatus] = useState({});
  const [weeklyPayments, setWeeklyPayments] = useState({});

  // Debtors Dialog State
  const [showDebtorsDialog, setShowDebtorsDialog] = useState(false);
  const [allDebtors, setAllDebtors] = useState([]);
  const [isLoadingDebtors, setIsLoadingDebtors] = useState(false);

  // Audit Report Dialog State
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditReportData, setAuditReportData] = useState(null);
  const [isFetchingAudit, setIsFetchingAudit] = useState(false);
  const [auditDayFilter, setAuditDayFilter] = useState(() => {
    const dayMap = { 1: 'M', 2: 'T', 3: 'W', 4: 'TH', 5: 'F' };
    return dayMap[new Date().getDay()] || 'M';
  });

  useEffect(() => {
    studentsRef.current = students;
  }, [students]);

  const getCellKey = (studentId, dayKey) => `${studentId}_${dayKey}`;

  const getPaymentCoverage = (studentId) => {
    const studentPayments = weeklyPayments[studentId] || [];
    const coverage = {}; // dayIndex -> paymentRecord

    const selectedWeekObj = availableWeeks.find(w => w.label === week);
    if (!selectedWeekObj?.startDate) return coverage;
    const weekStart = new Date(selectedWeekObj.startDate);

    studentPayments.forEach(p => {
      if (!p || !p.date) return;
      const paymentDate = new Date(p.date);
      const diffTime = paymentDate.getTime() - weekStart.getTime();
      const startIndex = Math.round(diffTime / (1000 * 60 * 60 * 24));

      const count = p.daysCount || 0;
      for (let i = 0; i < count; i++) {
        const coveredIndex = startIndex + i;
        if (coveredIndex >= 0 && coveredIndex < 5) {
          coverage[coveredIndex] = p;
        }
      }
    });
    return coverage;
  };

  const cycleCellStatus = async (student, enrollment, dateStr, dayKey) => {
    if (!enrollment) return;
    const key = getCellKey(student._id, dayKey);
    const current = markingStatus[key] || 'pending';

    let next;
    if (current === 'pending') next = 'picked';
    else if (current === 'picked') next = 'dropped';
    else if (current === 'dropped') next = 'absent';
    else next = 'pending';

    setMarkingStatus(prev => ({ ...prev, [key]: next }));

    // Find day index
    const selectedWeekObj = availableWeeks.find(w => w.label === week);
    const baseWeekStart = selectedWeekObj ? new Date(selectedWeekObj.startDate) : startOfWeek(selectedDate, { weekStartsOn: 1 });
    const targetDate = new Date(dateStr);
    targetDate.setHours(0,0,0,0);
    const startD = new Date(baseWeekStart);
    startD.setHours(0,0,0,0);
    const diff = Math.round((targetDate - startD) / (1000 * 60 * 60 * 24));
    
    let resolvedDayKey = dayKey || "M";
    if (!dayKey && diff >= 0 && diff <= 4) {
      resolvedDayKey = ["M", "T", "W", "TH", "F"][diff];
    }

    let backendStatus = 'notmarked';
    if (next === 'picked') backendStatus = 'boarded';
    if (next === 'dropped') backendStatus = 'dropped';
    if (next === 'absent') backendStatus = 'absent';

    try {
      setIsSubmitting(true);
      await api.post('/transport/teacher/mark-transport', {
        student: student._id,
        termId: selectedTermId,
        academicYear: academicYear,
        week: parseInt(week.replace(/\D/g, ''), 10) || 1,
        day: resolvedDayKey,
        date: dateStr,
        status: backendStatus,
        routeSnapshot: enrollment?.route?.name || 'Manual Route',
        stopSnapshot: enrollment?.dropOffStop || enrollment?.stop || 'Manual Stop',
      });
    } catch (err) {
      setMarkingStatus(prev => ({ ...prev, [key]: current }));
      toast.error('Failed to update status');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enrollment State
  const [routes, setRoutes] = useState([]);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [enrollStudent, setEnrollStudent] = useState(null);
  const [enrollClassId, setEnrollClassId] = useState('');
  const [enrollSearch, setEnrollSearch] = useState('');
  const [enrollStop, setEnrollStop] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);

  const openEnrollModal = (student = null, enrollment = null) => {
    setEnrollStudent(student);
    setEnrollSearch('');
    if (student) {
      const classId = student.class?._id || student.class || '';
      if (classId) setEnrollClassId(classId.toString());
    }
    setEnrollStop(enrollment?.stop || '');
    setEnrollModalOpen(true);
  };

  // Assign Teacher State
  const [assignmentsDialogOpen, setAssignmentsDialogOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignRouteId, setAssignRouteId] = useState('');
  const [autoFilledRoute, setAutoFilledRoute] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [allTeachers, setAllTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isDeletingAssignment, setIsDeletingAssignment] = useState(false);

  const currentUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  })();

  const schoolId = currentUser?.school?._id || currentUser?.school || null;
  const canManageAssignments = currentUser?.role === 'admin';

  const fetchClasses = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/classes', { params: { school: schoolId } });
      const dataArray = Array.isArray(res.data) ? res.data : (res.data.classes || []);
      setClasses(dataArray);
      if (dataArray.length > 0) setEnrollClassId(prev => prev || dataArray[0]._id);
    } catch (err) {
      toast.error('Failed to load classes');
    }
  }, [schoolId]);

  const fetchAcademicYears = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/term/years', { params: { school: schoolId } });
      const years = res.data?.academicYears || [];
      if (years.length > 0) {
        const sortedYears = years.sort((a, b) => b.localeCompare(a));
        setAvailableAcademicYears(sortedYears);
        setAcademicYear(current => current || sortedYears[0]);
      }
    } catch (err) {
      toast.error('Unable to load academic years');
    }
  }, [schoolId]);

  const fetchTerms = useCallback(async () => {
    if (!schoolId) return;
    try {
      // Fetch all terms to find the globally active term
      const res = await api.get('/term/all', { params: { school: schoolId } });
      const termsData = res.data?.allTerms || [];
      const currentActiveTerm = termsData.find(t => t.isCurrent);
      setAllTerms(termsData);
      
      let targetYear = academicYear;
      if (!academicYear && currentActiveTerm) {
        if (userSelectedAcademicYearRef.current) return;
        targetYear = currentActiveTerm.academicYear;
        setAcademicYear(current => current || targetYear);
      }

      const filteredTerms = targetYear ? termsData.filter(t => t.academicYear === targetYear) : termsData;
      const uniqueTerms = [...new Set(filteredTerms.map(t => t.term))].sort();
      setAvailableTerms(uniqueTerms);

      const targetCurrentTerm = filteredTerms.find(t => t.isCurrent) || filteredTerms[0];

      if (filteredTerms.length > 0 && !term) {
        setTerm(targetCurrentTerm.term);
        setSelectedTermId(targetCurrentTerm._id);
      } else if (term) {
        const match = filteredTerms.find(t => t.term === term);
        if (match) setSelectedTermId(match._id);
      }
    } catch (err) {
      toast.error('Unable to load terms');
    }
  }, [schoolId, academicYear, term]);

  const fetchTermWeeks = useCallback(async () => {
    if (!term || !academicYear || !selectedTermId || !schoolId) return;
    try {
      const res = await api.get('/term/weeks', {
        params: { school: schoolId, academicYear, term, termId: selectedTermId }
      });
      const weeks = res.data?.weeks || [];
      setAvailableWeeks(weeks);
      const currentActiveWeek = weeks.find(w => w.isCurrent);
      if (weeks.length > 0 && !week) {
        setWeek(currentActiveWeek ? currentActiveWeek.label : weeks[0].label);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setAvailableWeeks([]);
        setWeek('');
      } else {
        toast.error('Unable to load weeks');
      }
    }
  }, [schoolId, academicYear, term, selectedTermId, week]);

  const fetchRoutes = useCallback(async () => {
    try {
      const res = await api.get('/transport/routes');
      setRoutes(res.data.routes || []);
    } catch (err) {
      toast.error('Failed to load routes');
    }
  }, []);

  const fetchTeachers = useCallback(async () => {
    try {
      const res = await api.get('/teachers');
      const dataArray = Array.isArray(res.data) ? res.data : (res.data?.teachers || []);
      setAllTeachers(Array.isArray(dataArray) ? dataArray : []);
    } catch (err) {
      console.error('Failed to load teachers for assignment', err);
    }
  }, []);

  useEffect(() => {
    if (term && allTerms.length > 0) {
      const selectedTerm = allTerms.find(t => t.term === term && t.academicYear === academicYear);
      if (selectedTerm) setSelectedTermId(selectedTerm._id);
    }
  }, [term, academicYear, allTerms]);

  useEffect(() => {
    if (schoolId) {
      // Aggressively pre-fill current term and week using the global timeline endpoint
      api.get('/term/current-week', { params: { school: schoolId } })
        .then(res => {
          if (res.data?.success) {
            const cw = res.data.data;
            if (userSelectedAcademicYearRef.current) return;
            setAcademicYear(cw.academicYear);
            setTerm(cw.termName);
            setSelectedTermId(cw.termId);
            if (cw.current > 0) setWeek(`Week ${cw.current}`);
          }
        }).catch(err => console.error('Auto-select error:', err));
    }
    fetchAcademicYears();
    fetchClasses();
  }, [schoolId, fetchAcademicYears, fetchClasses]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  useEffect(() => {
    fetchTermWeeks();
  }, [fetchTermWeeks]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const fetchAssignments = async () => {
    try {
      const res = await api.get('/transport/assignments');
      setAssignments(res.data.assignments || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, []);

  const applyBoardingSnapshots = useCallback((targetStudents, fetchedRecords, fetchedPayments = null) => {
    setAttendanceRecords(fetchedRecords);

    if (Array.isArray(fetchedPayments)) {
      const pMap = {};
      fetchedPayments.forEach(p => {
        const sid = (p.student?._id || p.student)?.toString();
        if (sid) {
          if (!pMap[sid]) pMap[sid] = [];
          pMap[sid].push(p);
        }
      });
      setWeeklyPayments(pMap);
    }

    const breakdownByStudent = new Map();
    fetchedRecords.forEach(record => {
      (record?.breakdown || []).forEach(entry => {
        const sid = (entry.student?._id || entry.student)?.toString();
        if (sid && !breakdownByStudent.has(sid)) {
          breakdownByStudent.set(sid, entry);
        }
      });
    });

    const seeded = {};
    const dayKeys = ["M", "T", "W", "TH", "F"];

    targetStudents.forEach(student => {
      const sid = student._id.toString();
      const entry = breakdownByStudent.get(sid);

      dayKeys.forEach((dayKey) => {
        const key = `${sid}_${dayKey}`;
        if (entry && entry.days && entry.days[dayKey]) {
          const status = entry.days[dayKey];
          if (status === 'boarded') seeded[key] = 'picked';
          else if (status === 'dropped') seeded[key] = 'dropped';
          else if (status === 'absent') seeded[key] = 'absent';
          else seeded[key] = 'pending';
        } else {
          seeded[key] = 'pending';
        }
      });
    });

    setMarkingStatus(seeded);
  }, []);

  const applyWeeklyPayments = useCallback((fetchedPayments) => {
    const pMap = {};
    (fetchedPayments || []).forEach(p => {
      const sid = (p.student?._id || p.student)?.toString();
      if (sid) {
        if (!pMap[sid]) pMap[sid] = [];
        pMap[sid].push(p);
      }
    });
    setWeeklyPayments(pMap);
  }, []);

  const fetchLiveBoardingData = useCallback(async (targetStudents = studentsRef.current, options = {}) => {
    if (!selectedTermId || !week) return;

    const { includePayments = false, signal } = options;
    const weekNum = parseInt(week.replace(/\D/g, ''), 10) || 1;

    const requests = [
      api.get('/transport/teacher/transport-fee-records', {
        params: { termId: selectedTermId, week: weekNum, _t: Date.now() },
        signal,
      }),
    ];

    if (includePayments) {
      requests.push(
        api.get('/transport/teacher/weekly-fee-payments', {
          params: { termId: selectedTermId, weekLabel: week, _t: Date.now() },
          signal,
        }),
      );
    }

    const [recordsRes, paymentsRes] = await Promise.all(requests);
    const fetchedRecords = Array.isArray(recordsRes.data?.data) ? recordsRes.data.data : [];
    const fetchedPayments = includePayments ? (paymentsRes?.data?.payments || []) : null;

    applyBoardingSnapshots(targetStudents, fetchedRecords, fetchedPayments);
  }, [applyBoardingSnapshots, selectedTermId, week]);

  const fetchBoardingData = useCallback(async (isBackground = false) => {
    if (!academicYear || !selectedTermId || !week) return;

    if (isBackground) {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      if (backgroundFetchInFlightRef.current) {
        return;
      }
      backgroundFetchInFlightRef.current = true;
    } else if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    if (!isBackground) {
      abortControllerRef.current = controller;
    }

    try {
      if (!isBackground) {
        setLoading(true);

        const [studentsRes, enrollmentsRes, paymentsRes] = await Promise.all([
          api.get('/students', { signal: controller.signal }),
          api.get('/transport/enrollments', { signal: controller.signal }),
          api.get('/transport/teacher/weekly-fee-payments', { params: { termId: selectedTermId, weekLabel: week, _t: Date.now() }, signal: controller.signal }),
        ]);

        if (controller.signal.aborted) return;

        const fetchedStudents = Array.isArray(studentsRes.data) ? studentsRes.data : (studentsRes.data.students || []);
        const fetchedEnrollments = enrollmentsRes.data.enrollments || [];
        const activeEnrollmentIds = new Set(
          fetchedEnrollments
            .filter(e => e.status === 'active')
            .map(e => (e.student?._id || e.student)?.toString())
            .filter(Boolean)
        );
        const enrolledStudents = fetchedStudents
          .filter(student => activeEnrollmentIds.has(student._id?.toString()))
          .sort((a, b) => {
            const classA = a.class?.displayName || a.class?.name || '';
            const classB = b.class?.displayName || b.class?.name || '';
            if (classA !== classB) return classA.localeCompare(classB);
            const nameA = a.user?.name || a.name || '';
            const nameB = b.user?.name || b.name || '';
            return nameA.localeCompare(nameB);
          });

        setAllStudents(fetchedStudents);
        setStudents(enrolledStudents);
        setEnrollments(fetchedEnrollments);
        await fetchLiveBoardingData(enrolledStudents, {
          includePayments: false,
          signal: controller.signal,
        });

        const fetchedPayments = paymentsRes.data?.payments || [];
        applyWeeklyPayments(fetchedPayments);
        return;
      }

      await fetchLiveBoardingData(studentsRef.current, { signal: controller.signal });
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;

      if (!isBackground) {
        const now = Date.now();
        if (now - lastNetworkToastAtRef.current > 10000) {
          toast.error('Failed to load boarding data');
          lastNetworkToastAtRef.current = now;
        }
      }

      console.error(err);
    } finally {
      if (isBackground) {
        backgroundFetchInFlightRef.current = false;
      }
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [academicYear, selectedTermId, week, fetchLiveBoardingData, applyWeeklyPayments]);

  useEffect(() => {
    if (academicYear && selectedTermId && week && availableWeeks.length > 0) {
      fetchBoardingData();

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          fetchBoardingData(true);
        }
      };

      const intervalId = setInterval(() => {
        fetchBoardingData(true);
      }, BOARDING_POLL_INTERVAL_MS);

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(intervalId);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [academicYear, selectedTermId, week, availableWeeks, fetchBoardingData]);

  const handleTeacherSelect = async (teacher) => {
    setSelectedTeacher(teacher);
    setAutoFilledRoute(false);
    const teacherUserId = teacher.user?._id || teacher._id;
    try {
      const res = await api.get('/transport/assignments', {
        params: { teacherId: teacherUserId }
      });
      const existing = (res.data.assignments || []);
      if (existing.length > 0) {
        const latestAssignment = existing[0];
        if (latestAssignment.route?._id) {
          setAssignRouteId(latestAssignment.route._id);
          setAutoFilledRoute(true);
        }
      }
    } catch (err) {
      console.error('Could not auto-fill route', err);
    }
  };

  const handleAssignTeacher = async () => {
    if (!selectedTeacher || !selectedTermId) {
      toast.warning('Please select a teacher to assign.');
      return;
    }
    try {
      setIsAssigning(true);
      const teacherUserId = selectedTeacher.user?._id || selectedTeacher._id;

      let globalRouteId = routes.find(r => r.name === "Single Bus Route")?._id || routes[0]?._id;
      if (!globalRouteId) {
        const routeRes = await api.post('/transport/routes', { name: "Single Bus Route", stops: ["Default Stop"], defaultFee: 0 });
        globalRouteId = routeRes.data.route._id;
        fetchRoutes();
      }

      const assignRes = await api.post('/transport/assignments', {
        teacherId: teacherUserId,
        routeId: globalRouteId,
        termId: selectedTermId
      });

      const resolvedRoute = routes.find(r => r._id === globalRouteId) || { _id: globalRouteId, name: 'Single Bus Route' };
      const normalizedAssignment = {
        ...(assignRes.data?.assignment || {}),
        route: assignRes.data?.assignment?.route || resolvedRoute,
        teacher: selectedTeacher,
        term: assignRes.data?.assignment?.term || selectedTermId,
      };

      setAssignments(prev => {
        const filtered = (prev || []).filter(existing => {
          const existingRouteId = existing.route?._id || existing.route;
          return String(existingRouteId) !== String(globalRouteId);
        });
        return [...filtered, normalizedAssignment];
      });

      toast.success('Teacher authorized successfully!');
      setAssignModalOpen(false);
      setAssignmentsDialogOpen(false);
      setSelectedTeacher(null);
      setTeacherSearch('');
      setAssignRouteId('');
      setAutoFilledRoute(false);
      await fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign teacher');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    if (!assignment?._id || isDeletingAssignment) return;

    const teacherName = assignment.teacher?.user?.name || assignment.teacher?.name || 'this teacher';
    const confirmed = window.confirm(`Delete authorization for ${teacherName}?`);

    if (!confirmed) return;

    try {
      setIsDeletingAssignment(true);
      await api.delete(`/transport/assignments/${assignment._id}`);
      await fetchAssignments();
      const deletedTeacherId = String(assignment.teacher?._id || assignment.teacher || '');
      const currentTeacherId = String(selectedTeacher?._id || '');
      if (deletedTeacherId && currentTeacherId === deletedTeacherId) {
        setSelectedTeacher(null);
        setAssignRouteId('');
        setAutoFilledRoute(false);
      }
      toast.success('Teacher authorization deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete teacher authorization');
    } finally {
      setIsDeletingAssignment(false);
    }
  };

  const handleEnrollStudent = async (statusOrEvent = 'active') => {
    const status = typeof statusOrEvent === 'string' ? statusOrEvent : 'active';

    if (!enrollStudent || !selectedTermId || (!enrollStop.trim() && status === 'active')) {
      toast.warning('Please enter a pickup location/address.');
      return;
    }

    try {
      setIsEnrolling(true);

      let globalRouteId = routes.find(r => r.name === "Single Bus Route")?._id || routes[0]?._id;
      if (!globalRouteId) {
        const routeRes = await api.post('/transport/routes', { name: "Single Bus Route", stops: ["Default Stop"], defaultFee: 0 });
        globalRouteId = routeRes.data.route._id;
        fetchRoutes();
      }

      await api.post('/transport/enrollments', {
        studentId: enrollStudent._id,
        termId: selectedTermId,
        academicYear,
        routeId: globalRouteId,
        stop: enrollStop.trim(),
        status
      });

      toast.success(status === 'active' ? 'Student enrolled successfully!' : 'Student unenrolled successfully!');
      setEnrollModalOpen(false);
      setEnrollStudent(null);
      setEnrollSearch('');
      setEnrollStop('');
      fetchBoardingData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to complete update');
      console.error('Enrollment failed', err.response?.data || err);
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleSaveFee = async (enrollmentId, amount) => {
    try {
      await api.put(`/transport/enrollments/${enrollmentId}/fee`, { feeAmount: amount });
      toast.success('Fee updated');
      setEnrollments(prev => prev.map(e => e._id === enrollmentId ? { ...e, feeAmount: amount } : e));
    } catch (err) {
      toast.error('Failed to update fee');
    }
  };

  // Compute stats
  const enrolledStudents = students.length;

  const todayStr = format(selectedDate, 'yyyy-MM-dd');
  const todayIndex = new Date(todayStr).getDay(); // 0 is Sun, 1 is Mon
  const activeDayKey = todayIndex >= 1 && todayIndex <= 5 ? ["M", "T", "W", "TH", "F"][todayIndex - 1] : "M";
  const activeEnrollmentsByStudent = new Map(
    enrollments
      .filter(enrollment => enrollment.status === 'active')
      .map(enrollment => [
        (enrollment.student?._id || enrollment.student)?.toString(),
        enrollment
      ])
      .filter(([studentId]) => Boolean(studentId))
  );

  const currentlyBoarded = students.filter(s => {
    const key = `${s._id?.toString()}_${activeDayKey}`;
    return markingStatus[key] === 'picked';
  }).length;
  
  const droppedOffToday = students.filter(s => {
    const key = `${s._id?.toString()}_${activeDayKey}`;
    return markingStatus[key] === 'dropped';
  }).length;

  const notBoardedToday = enrolledStudents - currentlyBoarded - droppedOffToday;
  const getDateStrForDayKey = useCallback((dayKey) => {
    const dayOffset = { M: 0, T: 1, W: 2, TH: 3, F: 4 }[dayKey] || 0;
    const selectedWeekObj = availableWeeks.find(w => w.label === week);
    const baseWeekStart = selectedWeekObj ? new Date(selectedWeekObj.startDate) : startOfWeek(selectedDate, { weekStartsOn: 1 });
    return format(addDays(baseWeekStart, dayOffset), 'yyyy-MM-dd');
  }, [availableWeeks, selectedDate, week]);
  const activeDateStr = getDateStrForDayKey(activeDayKey);
  const auditDateStr = getDateStrForDayKey(auditDayFilter);

  const todayPayments = Object.values(weeklyPayments).flat().filter(p => {
    if (!p || !p.date) return false;
    return format(new Date(p.date), 'yyyy-MM-dd') === activeDateStr;
  });

  const studentsWithBusFeePaidToday = new Set(todayPayments.map(p => (p.student?._id || p.student)?.toString())).size;
  const collectedBusFeeToday = todayPayments.reduce((sum, p) => sum + (Number(p.totalAmount) || 0), 0);

  const boardedStudents = students.filter(student => {
    const key = `${student._id?.toString()}_${activeDayKey}`;
    return markingStatus[key] === 'picked' || markingStatus[key] === 'dropped';
  });

  const studentsWithExpectedFeeToday = boardedStudents.filter(student => {
    const enrollment = activeEnrollmentsByStudent.get(student._id?.toString());
    return (Number(enrollment?.feeAmount ?? student.feeAmount) || 0) > 0;
  }).length;

  const expectedBusFeeToday = boardedStudents.reduce((sum, student) => {
    const enrollment = activeEnrollmentsByStudent.get(student._id?.toString());
    return sum + (Number(enrollment?.feeAmount ?? student.feeAmount) || 0);
  }, 0);

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';
  const isAuditStudentPaid = useCallback((student) => {
    const normalizedStatus = String(student?.status || '').toLowerCase();

    if (['present', 'paid', 'settled', 'completed'].includes(normalizedStatus)) {
      return true;
    }

    if (['absent', 'unpaid', 'pending'].includes(normalizedStatus)) {
      return false;
    }

    if (typeof student?.isPaid === 'boolean') {
      return student.isPaid;
    }

    if (typeof student?.paid === 'boolean') {
      return student.paid;
    }

    if (student?.payment || student?.paymentRecord || student?.paymentId) {
      return true;
    }

    return false;
  }, []);

  const fetchDebtors = useCallback(async () => {
    if (!selectedTermId || !activeDateStr) return;
    try {
      setIsLoadingDebtors(true);
      const res = await api.get('/transport/debtors', {
        params: { termId: selectedTermId, week, activeDayKey, dateStr: activeDateStr }
      });
      if (res.data?.success) {
        setAllDebtors(res.data.debtors || []);
      }
    } catch (err) {
      console.error('Failed to fetch transport debtors:', err);
      toast.error('Failed to load debtors list');
    } finally {
      setIsLoadingDebtors(false);
    }
  }, [selectedTermId, week, activeDayKey, activeDateStr]);

  const fetchAuditReport = useCallback(async (dayKey) => {
    const day = dayKey || auditDayFilter;
    const dateStr = getDateStrForDayKey(day);
    if (!selectedTermId || !week || !dateStr) return;
    try {
      setIsFetchingAudit(true);
      const res = await api.get('/transport/audit-report', {
        params: { termId: selectedTermId, week, activeDayKey: day, dateStr }
      });
      if (res.data?.success) {
        setAuditReportData(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch transport audit report:', err);
      toast.error('Failed to load audit report');
    } finally {
      setIsFetchingAudit(false);
    }
  }, [selectedTermId, week, auditDayFilter, getDateStrForDayKey]);

  useEffect(() => { if (showDebtorsDialog) fetchDebtors(); }, [showDebtorsDialog, fetchDebtors]);
  useEffect(() => { if (showAuditDialog) fetchAuditReport(); }, [showAuditDialog, fetchAuditReport]);

  const classFilteredStudents = allStudents
    .filter(student => {
      const classId = student.class?._id || student.class;
      const matchesClass = enrollClassId ? classId?.toString() === enrollClassId.toString() : true;
      const matchesSearch = (student.user?.name || student.name || '')
        .toLowerCase()
        .includes(enrollSearch.toLowerCase());
      return matchesClass && matchesSearch;
    })
    .sort((a, b) => (a.user?.name || a.name || '').localeCompare(b.user?.name || b.name || ''));

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Transport Boarding</>} description={<>Manage student boarding and teacher assignments</>} actions={<><Button variant="outlined" color="error" size={isMobile ? 'medium' : 'large'} startIcon={<MoneyOffIcon />} onClick={() => setShowDebtorsDialog(true)} disabled={!selectedTermId || !week}>
                    View Debtors
                  </Button>
        <Button variant="outlined" color="info" size={isMobile ? 'medium' : 'large'} startIcon={<SummarizeIcon />} onClick={() => {
          setAuditDayFilter(activeDayKey);
          setShowAuditDialog(true);
        }} disabled={!selectedTermId || !week}>
                    Audit Report
                  </Button>
        <Button variant="contained" onClick={fetchBoardingData} disabled={loading}>
                    Refresh
                  </Button></>}></PageHeader>
      {(() => {
        const cardSx = {
          boxShadow: isMobile ? 1 : 3,
          height: '100%',
          minHeight: isMobile ? 108 : 124,
          width: '100%',
          transition: 'all 0.3s ease',
          '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 }
        };
        return (
        <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>

        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 2.4 }}>
          <SummaryMetric sx={{ ...cardSx, border: `1px solid ${theme.palette.success.main}` }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <PaymentsIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Bus Fee Today</Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="success.main">
                    ¢{collectedBusFeeToday.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    From {studentsWithBusFeePaidToday} student(s) today<br/>
                    Expected: ¢{expectedBusFeeToday.toFixed(2)} ({studentsWithExpectedFeeToday})
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 2.4 }}>
          <SummaryMetric sx={{ ...cardSx, border: `1px solid ${theme.palette.info.main}` }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.info.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <TodayIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Boarded</Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="info.main">{currentlyBoarded}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 2.4 }}>
          <SummaryMetric sx={{ ...cardSx, border: `1px solid ${theme.palette.secondary.main}` }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <CheckCircleIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Dropped</Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="secondary.main">{droppedOffToday}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 2.4 }}>
          <SummaryMetric sx={{ ...cardSx, border: `1px solid ${theme.palette.warning.main}` }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.warning.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <RemoveCircleIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Absent/None</Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="warning.main">{notBoardedToday}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 2.4 }}>
          <SummaryMetric
            sx={{ ...cardSx, border: `1px solid ${theme.palette.success.main}`, cursor: 'pointer' }}
            onClick={() => openEnrollModal()}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <SchoolIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Enroll Students</Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="success.main">{enrolledStudents}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>Choose and enroll</Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 2.4 }}>
          <SummaryMetric
            sx={{ ...cardSx, border: `1px solid ${theme.palette.primary.main}`, cursor: 'pointer' }}
            onClick={() => setAssignmentsDialogOpen(true)}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <PersonAddIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>Authorized Teachers</Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">{assignments.length}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Tap to manage assignments
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>
         );
      })()}

      {/* Filter Card */}
      <FilterBar sx={{ mb: 4, boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 2}
            sx={{ mb: 3 }}
          >
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}>
              <SchoolIcon fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}>
              Transport Setup
            </Typography>
          </Stack>

          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Year"
                value={academicYear}
                onChange={(e) => {
                  userSelectedAcademicYearRef.current = true;
                  setAcademicYear(e.target.value);
                  setTerm('');
                  setWeek('');
                }}
                disabled={loading || availableAcademicYears.length === 0}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="" disabled>Select Academic Year</MenuItem>
                {availableAcademicYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Term"
                value={term}
                onChange={(e) => {
                  setTerm(e.target.value);
                  setWeek('');
                }}
                disabled={loading || availableTerms.length === 0}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="" disabled>Select Term</MenuItem>
                {availableTerms.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Week"
                value={availableWeeks.some(w => w.label === week) ? week : ''}
                onChange={(e) => setWeek(e.target.value)}
                disabled={loading || availableWeeks.length === 0}
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="" disabled>
                  {availableWeeks.length === 0 ? "No weeks" : "Select Week"}
                </MenuItem>
                {availableWeeks.map(w => (
                  <MenuItem key={w.label} value={w.label}>{w.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', minHeight: isMobile ? 56 : 64, display: 'flex', alignItems: 'center', px: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Manifest Scope
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    All enrolled students across classes
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            
            
            
          </Box>
        </CardContent>
      </FilterBar>

      {/* Stats Cards */}
      

      {/* Enrolled Students Table */}
      <Card sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}`, mb: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                Weekly Boarding Roster ({students.length})
              </Typography>
              {students.length > 0 && !isMobile && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip label={`${enrolledStudents} Enrolled`} color="success" variant="outlined" size="small" />
                </Stack>
              )}
            </Stack>
          </Box>

          {loading ? (
            <LoadingState />
          ) : students.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <BusIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                No students found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {term && week
                  ? `No enrolled transport students found for ${week} of ${term} ${academicYear}`
                  : 'Select filters to view students'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STUDENT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>AMOUNT(¢)</TableCell>
                    {Array.from({ length: 5 }).map((_, i) => {
                      const selectedWeekObj = availableWeeks.find(w => w.label === week);
                      const baseWeekStart = selectedWeekObj ? new Date(selectedWeekObj.startDate) : startOfWeek(selectedDate, { weekStartsOn: 1 });
                      const currentDay = addDays(baseWeekStart, i);
                      return (
                        <TableCell key={i} align="center" sx={{ fontWeight: 'bold', py: 2 }}>
                          {format(currentDay, 'EEE')}<br />
                          <Typography variant="caption" color="text.secondary">{format(currentDay, 'MMM d')}</Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
{students.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {students.map(student => {
                    const studentIdStr = student._id?.toString();
                    const fullEnrollment = enrollments.find(e => (e.student?._id ?? e.student)?.toString() === studentIdStr);
                    const enrollment = fullEnrollment?.status === 'active' ? fullEnrollment : null;

                    return (
                      <TableRow key={student._id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <StudentAvatar student={student} sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                              {student.user?.name?.[0] || student.name?.[0] || 'S'}
                            </StudentAvatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="500" sx={{ cursor: 'pointer' }} onClick={() => openEnrollModal(student, fullEnrollment)}>
                                {student.user?.name || student.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(student.class?.displayName || student.class?.name || 'No Class')} • {enrollment?.stop || 'No stop saved'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell align="center">
                          {enrollment ? (
                             <Stack alignItems="center" spacing={1}>
                               <TextField
                                 size="small"
                                 type="number"
                                 inputProps={{ min: 0, step: "0.01" }}
                                 defaultValue={enrollment.feeAmount || 0}
                                 onBlur={(e) => handleSaveFee(enrollment._id, e.target.value)}
                                 sx={{ width: 80, '& .MuiInputBase-input': { textAlign: 'center' } }}
                               />
                               {weeklyPayments[studentIdStr]?.length > 0 && (
                                 <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                   Paid: ¢{(weeklyPayments[studentIdStr] || []).reduce((sum, p) => sum + (Number(p.totalAmount) || (Number(p.daysCount) || 0) * (enrollment.feeAmount || 0)), 0).toFixed(2)}
                                 </Typography>
                               )}
                             </Stack>
                          ) : (
                             <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>

                        {Array.from({ length: 5 }).map((_, i) => {
                          const dayKey = ["M", "T", "W", "TH", "F"][i];
                          const selectedWeekObj = availableWeeks.find(w => w.label === week);
                          const baseWeekStart = selectedWeekObj ? new Date(selectedWeekObj.startDate) : startOfWeek(selectedDate, { weekStartsOn: 1 });
                          const currentDate = addDays(baseWeekStart, i);
                          const dateStr = format(currentDate, 'yyyy-MM-dd');

                          const key = `${studentIdStr}_${dayKey}`;
                          const cellStatus = markingStatus[key] || 'pending';

                          let btnColor = 'inherit';
                          let btnLabel = 'TAP TO BOARD';
                          let btnIcon = <BusIcon sx={{ fontSize: '1.2rem' }} />;
                          let animSx = {};

                          if (cellStatus === 'dropped') {
                            btnColor = 'success'; btnLabel = 'DROPPED';
                            btnIcon = <CheckCircleIcon sx={{ fontSize: '1.2rem' }} />;
                            animSx = { animation: `${pulseGreen} 1.5s ease-out` };
                          } else if (cellStatus === 'picked') {
                            btnColor = 'primary'; btnLabel = 'BOARDED';
                            btnIcon = <CheckCircleIcon sx={{ fontSize: '1.2rem' }} />;
                          } else if (cellStatus === 'absent') {
                            btnColor = 'error'; btnLabel = 'ABSENT';
                            btnIcon = <CancelIcon sx={{ fontSize: '1.2rem' }} />;
                          }

                          const isPaid = !!getPaymentCoverage(studentIdStr)[i];

                          return (
                            <TableCell key={i} align="center" sx={{ py: 1, backgroundColor: cellStatus === 'pending' ? '#f5f5f5' : 'transparent' }}>
                              {!enrollment ? (
                                <Tooltip title="Click to enroll student" arrow>
                                  <Typography 
                                    variant="caption" 
                                    color="primary" 
                                    onClick={() => openEnrollModal(student, fullEnrollment)}
                                    sx={{ 
                                      cursor: 'pointer', 
                                      fontWeight: 'bold',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                  >
                                    Enroll
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Stack alignItems="center" spacing={0.5}>
                                  <Tooltip title="Click to cycle: Pending → Boarded → Dropped → Absent" arrow>
                                    <Button
                                      size="small"
                                      color={btnColor === 'inherit' ? 'primary' : btnColor}
                                      variant={cellStatus === 'pending' ? 'outlined' : 'contained'}
                                      startIcon={btnIcon}
                                      onClick={() => cycleCellStatus(student, enrollment, dateStr, dayKey)}
                                      disabled={isSubmitting}
                                      sx={{
                                        minWidth: 110,
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        color: cellStatus === 'pending' ? 'text.secondary' : undefined,
                                        borderColor: cellStatus === 'pending' ? alpha(theme.palette.text.secondary, 0.3) : undefined,
                                        boxShadow: cellStatus !== 'pending' ? 2 : 0,
                                        ...animSx,
                                        '&:hover': { opacity: 0.9, transform: 'scale(1.02)', transition: 'all 0.15s ease' }
                                      }}
                                    >
                                      {btnLabel}
                                    </Button>
                                  </Tooltip>
                                  {isPaid && (
                                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '0.65rem' }}>
                                      ✓ PAID
                                    </Typography>
                                  )}
                                </Stack>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Enrollment Modal */}
      <Dialog open={enrollModalOpen} onClose={() => setEnrollModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {enrollStudent ? `Enroll ${enrollStudent?.user?.name || enrollStudent?.name}` : 'Select Class & Student'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {!enrollStudent && (
              <>
                <TextField
                  select
                  fullWidth
                  label="Class"
                  value={enrollClassId}
                  onChange={(e) => setEnrollClassId(e.target.value)}
                >
                  {classes.map(c => (
                    <MenuItem key={c._id} value={c._id}>{c.displayName || c.name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search student name..."
                  value={enrollSearch}
                  onChange={(e) => setEnrollSearch(e.target.value)}
                  InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> }}
                />

                <Paper variant="outlined" sx={{ p: 1, maxHeight: 260, overflowY: 'auto' }}>
                  {classFilteredStudents.map(student => {
                    const activeEnrollment = enrollments.find(e => (e.student?._id ?? e.student)?.toString() === student._id?.toString() && e.status === 'active');
                    return (
                      <Box
                        key={student._id}
                        onClick={() => {
                          setEnrollStudent(student);
                          setEnrollStop(activeEnrollment?.stop || '');
                        }}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': { bgcolor: 'action.hover' },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {student.user?.name || student.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {student.class?.displayName || student.class?.name || 'No Class'}
                          </Typography>
                        </Box>
                        {activeEnrollment ? (
                          <Chip label="Enrolled" color="success" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Not Enrolled" color="warning" size="small" variant="outlined" />
                        )}
                      </Box>
                    );
                  })}

                  {classFilteredStudents.length === 0 && (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                      No students found for this class.
                    </Typography>
                  )}
                </Paper>
              </>
            )}

            {enrollStudent && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setEnrollStudent(null);
                  setEnrollStop('');
                }}
                sx={{ alignSelf: 'flex-start' }}
              >
                Choose another student
              </Button>
            )}

            <TextField
              fullWidth
              label="Pickup Location / Home Address"
              placeholder="e.g., House 4, East Legon or Beside Blue Kiosk"
              value={enrollStop}
              onChange={(e) => setEnrollStop(e.target.value)}
              size="medium"
              disabled={!enrollStudent}
            />
            {enrollStudent && enrollments.some(e => (e.student?._id ?? e.student)?.toString() === enrollStudent._id?.toString() && e.status === 'active') && (
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), border: `1px dashed ${theme.palette.error.main}`, borderRadius: 1 }}>
                 <Typography variant="body2" color="error.main" gutterBottom fontWeight="bold">
                    Terminate Enrollment
                 </Typography>
                 <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    If this student is no longer using the bus, you can unenroll them. They will be removed from the daily passenger manifest.
                 </Typography>
                 <Button 
                   size="small" 
                   color="error" 
                   variant="outlined" 
                   startIcon={<RemoveCircleIcon />}
                   onClick={() => handleEnrollStudent('inactive')}
                   disabled={isEnrolling}
                 >
                   Unenroll Student
                 </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEnrollModalOpen(false);
            setEnrollStudent(null);
            setEnrollStop('');
            setEnrollSearch('');
          }} disabled={isEnrolling}>Cancel</Button>
          <Button onClick={() => handleEnrollStudent()} variant="contained" disabled={isEnrolling || !enrollStudent || !enrollStop.trim()}>
            {isEnrolling ? 'Enrolling...' : 'Enroll Student'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignmentsDialogOpen} onClose={() => setAssignmentsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Teacher Bus Assignments</DialogTitle>
        <DialogContent dividers>
          <DataTable component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Assigned Teacher</TableCell>
                  {canManageAssignments && (
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManageAssignments ? 2 : 1} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No teachers assigned yet.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments.map(assignment => (
                    <TableRow key={assignment._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24 }} src={assignment.teacher?.user?.profilePicture || assignment.teacher?.profilePicture} />
                          <Typography variant="body2">{assignment.teacher?.user?.name || assignment.teacher?.name || 'Unknown Teacher'}</Typography>
                        </Box>
                      </TableCell>
                      {canManageAssignments && (
                        <TableCell align="center">
                          <Tooltip title="Delete authorization">
                            <span>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteAssignment(assignment)}
                                disabled={isDeletingAssignment}
                              >
                                <RemoveCircleIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </DataTable>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentsDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setAssignmentsDialogOpen(false);
              setAssignModalOpen(true);
            }}
          >
            Authorize Teacher
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Teacher Modal */}
      <Dialog open={assignModalOpen} onClose={() => setAssignModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Authorize Bus Monitor (Teacher)</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Typography variant="body2" color="info.main" sx={{ fontStyle: 'italic', bgcolor: 'info.50', p: 1.5, borderRadius: 1 }}>
              Note: Since your school uses a strict single-bus system, authorizing a teacher gives them full access to the unified passenger manifest for the day.
            </Typography>

            {autoFilledRoute && (
              <Chip label="Teacher has an existing authorization for this term." color="info" size="small" sx={{ alignSelf: 'flex-start' }} />
            )}

            <TextField
              fullWidth
              size="small"
              placeholder="Search Teacher Name..."
              value={teacherSearch}
              onChange={(e) => setTeacherSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> }}
            />

            <Paper variant="outlined" sx={{ p: 1, maxHeight: 250, overflowY: 'auto' }}>
              {(allTeachers || [])
                .filter(t => (t.user?.name || t.name || '').toLowerCase().includes(teacherSearch.toLowerCase()))
                .map(teacher => {
                  const isSelected = selectedTeacher?._id === teacher._id;
                  const teacherName = teacher.user?.name || teacher.name || 'Unknown Teacher';
                  const teacherAvatar = teacher.user?.profilePicture || teacher.profilePicture;
                  return (
                    <Box
                      key={teacher._id}
                      onClick={() => handleTeacherSelect(teacher)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        mb: 0.5,
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        border: isSelected ? '1px solid' : '1px solid transparent',
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        '&:hover': { bgcolor: isSelected ? 'primary.50' : 'action.hover' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Avatar src={teacherAvatar} sx={{ width: 32, height: 32 }} />
                      <Typography variant="body2" fontWeight={isSelected ? "bold" : "regular"}>
                        {teacherName}
                      </Typography>
                    </Box>
                  );
                })}
              {(allTeachers || []).filter(t => (t.user?.name || t.name || '').toLowerCase().includes(teacherSearch.toLowerCase())).length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  No teachers found.
                </Typography>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalOpen(false)} disabled={isAssigning}>Cancel</Button>
          <Button onClick={handleAssignTeacher} variant="contained" disabled={isAssigning || !selectedTeacher}>
            {isAssigning ? 'Assigning...' : 'Confirm Assignment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Debtors Dialog ── */}
      <Dialog open={showDebtorsDialog} onClose={() => setShowDebtorsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyOffIcon color="error" />
          Transport Debtors — {activeDateStr}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Students who boarded the bus on <strong>{activeDayKey}</strong> ({activeDateStr}) but have no recorded fee payment for that day.
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {isLoadingDebtors ? (
            <LoadingState />
          ) : allDebtors.length > 0 ? (
            <DataTable><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Guardian Contact</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
{allDebtors.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                {allDebtors.map((student, idx) => (
                  <TableRow key={`${student.studentId}-${idx}`}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <StudentAvatar student={student} sx={{ width: 32, height: 32, bgcolor: theme.palette.error.main, fontSize: '0.75rem' }}>
                          {getInitials(student.studentName)}
                        </StudentAvatar>
                        <Typography variant="body2">{student.studentName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{student.className || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip label="Unpaid" size="small" color="error" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {student.guardianPhone ? (
                        <Box>
                          <Box
                            component="a"
                            href={`tel:${student.guardianPhone}`}
                            sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                          >
                            {student.guardianPhone}
                          </Box>
                          {student.guardianName && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {student.guardianName}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Not available</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></DataTable>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>No unpaid boarders found for {activeDayKey} ({activeDateStr}).</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowDebtorsDialog(false); fetchDebtors(); }}>Refresh</Button>
          <Button onClick={() => setShowDebtorsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Audit Report Dialog ── */}
      <Dialog open={showAuditDialog} onClose={() => setShowAuditDialog(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight="bold">Transport Daily Audit Report</Typography>
            <Tabs
              value={['M','T','W','TH','F'].indexOf(auditDayFilter)}
              onChange={(e, val) => {
                const newDay = ['M','T','W','TH','F'][val];
                setAuditDayFilter(newDay);
                fetchAuditReport(newDay);
              }}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
            >
              {['M','T','W','TH','F'].map(d => <Tab key={d} label={d} />)}
            </Tabs>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.paper', p: isMobile ? 1 : 4, '@media print': { p: 0, m: 0, border: 'none' } }}>
          <style>{`
            @media print {
              body * { visibility: hidden; }
              #printable-transport-audit, #printable-transport-audit * { visibility: visible; }
              #printable-transport-audit { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
              .MuiDialogActions-root { display: none !important; }
              .MuiDialogTitle-root { display: none !important; }
              .MuiDialog-paper { box-shadow: none !important; margin: 0 !important; width: 100% !important; max-width: none !important; max-height: none !important; }
            }
          `}</style>
          <Box id="printable-transport-audit">
            {isFetchingAudit ? (
              <LoadingState />
            ) : auditReportData && auditReportData.report ? (
              <Stack spacing={4} sx={{ color: '#000' }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', borderBottom: '2px solid #000', pb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" textTransform="uppercase">
                    Daily Transport Fee Audit Report
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {term} {academicYear} | Week: {week ? week.replace('Week ', '') : 'N/A'}
                  </Typography>
                  <Typography variant="subtitle2">
                    Reporting Day: {auditDayFilter} | Date: {auditReportData.dateStr || auditDateStr}
                  </Typography>
                </Box>

                {/* Executive Summary */}
                <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                  <DataTable><Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                        <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>EXECUTIVE SUMMARY</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc', width: '33%' }}>Total Paid Pupils</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc', width: '33%' }}>Total Unpaid Pupils</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '34%' }}>Grand Total Collected (¢)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{auditReportData.totalPaid}</TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{auditReportData.totalUnpaid}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                          {auditReportData.grandTotal?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table></DataTable>
                </Paper>

                {/* Per-class breakdowns */}
                {auditReportData.report.length === 0 ? (
                  <Typography textAlign="center" color="text.secondary" p={3}>
                    No boarded students recorded for this day.
                  </Typography>
                ) : auditReportData.report.map(clsReport => (
                  <Paper key={clsReport.className} elevation={0} sx={{ border: '1px solid #000', borderRadius: 0, mb: 3 }}>
                    <DataTable><Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                            CLASS: {clsReport.className}
                          </TableCell>
                          <TableCell align="right" colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                            Collected: ¢{clsReport.totalAmount?.toFixed(2)} | Paid: {clsReport.paidCount} | Unpaid: {clsReport.unpaidCount}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '40%' }}>Student Name</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '15%' }}>Status</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Amount (¢)</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '25%' }}>Parent Contact</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {clsReport.students.map(student => {
                          const paidStatus = isAuditStudentPaid(student);

                          return (
                            <TableRow key={student.studentId} sx={{ '& td': { borderBottom: '1px solid #eee' } }}>
                              <TableCell>{student.studentName}</TableCell>
                              <TableCell align="center" sx={{ color: paidStatus ? 'success.dark' : 'error.dark', fontWeight: 'bold' }}>
                                {paidStatus ? 'PAID' : 'UNPAID'}
                              </TableCell>
                              <TableCell align="right">{student.amount?.toFixed(2)}</TableCell>
                              <TableCell>
                                {student.guardianName || student.guardianPhone
                                  ? `${student.guardianName ? student.guardianName + ' — ' : ''}${student.guardianPhone || ''}`
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {clsReport.students.length === 0 && (
                          <TableRow><TableCell colSpan={4} align="center">No students recorded.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table></DataTable>
                  </Paper>
                ))}

                {/* Signatures */}
                <Box sx={{ mt: 6, pt: 4, display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #ccc' }}>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }} />
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Prepared By (Accountant)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }} />
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Approved By (Admin/Principal)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                </Box>
              </Stack>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAuditDialog(false)}>Close</Button>
          <Button variant="contained" onClick={() => window.print()}>Print Report</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
